import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { environment } from "src/environments/environment";

import { AuthService, SubscribersService } from "../services";
import { AuthRefreshService, AUTH_REFRESH_RETRIED } from "../services/auth/auth-refresh.service";
import { AlertService } from "../services";
import { isAiApiUrl } from "../utils/auth-client";
import { extractErrorMessage } from "../utils/error-message.util";

/**
 * Global HTTP error interceptor.
 *
 * Centralized routing:
 *   - All toast messages flow through {@link extractErrorMessage}, which
 *     prefers backend-provided text and falls back to `Constants.SOMETHING_WENT_WRONG`.
 *   - Status-specific branches only diverge on *navigation/side-effects*
 *     (clearing the session, redirecting, propagating to a component), not
 *     on the message itself.
 *   - 422 propagates to the component for inline validation handling.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private alertService: AlertService,
    private authService: AuthService,
    private subscribersService: SubscribersService,
    private authRefreshService: AuthRefreshService,
  ) {}

  /**
   * End the session exactly once for a burst of 401s, then propagate.
   *
   * `endSession()` is the single owner of the teardown: it returns false when a teardown is already
   * in flight or we are on a public auth page, so the toast and shell reset happen once per expiry
   * rather than once per failing request. The error still propagates either way, so each caller can
   * clear its own loading state.
   */
  private endSessionWith(message: string, err: unknown): Observable<never> {
    if (this.authService.endSession()) {
      this.alertService.error(message);
      this.subscribersService.changed_username.next("");
      this.subscribersService.userLoggedOut(true);
    }
    return throwError(() => err);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Caller opted out of the global error toast (e.g. polling a record mid
    // conversion where a transient "Quote does not exist" is expected). Strip
    // the marker header and propagate errors silently so the caller handles them.
    const skipError = request.headers.has('X-Skip-Error');
    if (skipError) {
      request = request.clone({ headers: request.headers.delete('X-Skip-Error') });
    }

    return next.handle(request).pipe(catchError((err) => {
      // Caller handles its own errors — no toast. But a 401 is still RENEWED first.
      //
      // "Skip error" means skip the reporting, not the session. The bootstrap probe
      // (AuthService.init → GET auth/me) is a skip-error request, and with a short access token a
      // returning admin's first request ALWAYS 401s while their session is perfectly renewable.
      // Rethrowing it untouched sent init() down its "not signed in" path, so reloading the
      // console — or opening it in a new tab — after the access token aged out signed the admin
      // out and demanded their password and code again, with seven days of session still left.
      // The customer site and the BA dashboard both renew on their bootstrap probe; this now does
      // too, under exactly the guards the 401 case below uses. Only if the renewal is refused does
      // the caller see the 401, and it handles that silently, as it always has.
      if (skipError) {
        const renewable = err?.status === 401
          && !isAiApiUrl(request.url, environment.aiApiUrl)
          && !request.context.get(AUTH_REFRESH_RETRIED)
          && !request.url.includes('auth/refresh-token')
          && !this.authService.isSessionEnding();
        if (renewable) {
          return this.authRefreshService.refresh().pipe(
            switchMap((renewed) => renewed
              ? next.handle(request.clone({ context: request.context.set(AUTH_REFRESH_RETRIED, true) }))
              : throwError(() => err))
          );
        }
        return throwError(() => err);
      }

      // Endpoints whose errors are handled by their caller (inline UX).
      //
      // The Pebbl email agent's draft and rating routes are here for the same
      // reason, plus one of their own: `GET /pebbl/email/draft/{thread_id}`
      // answers 404 when nothing is saved, which is the normal state for almost
      // every thread. Left to the global handler, simply opening a reply box
      // would toast "not found". The composer already reports what matters —
      // a stale draft, a rating that would not save — where the user is looking.
      if (
        request.url.includes('/contacts/suggest') ||
        request.url.includes('/gmail/drafts') ||
        request.url.includes('/pebbl/email/draft') ||
        request.url.includes('/pebbl/feedback/email-rating')
      ) {
        return throwError(() => err);
      }

      const pathArray = location.pathname.split('/');
      // Admin paths handle their own error UX — bail out.
      if (pathArray.length < 2 || pathArray[1] === 'admin') {
        return throwError(() => err);
      }

      // Validation errors propagate to the component (form-level handling).
      if (err.status === 422) {
        return throwError(() => err);
      }

      // Special case: quote-orders view component handles 500 itself.
      if (
        err.status === 500 &&
        pathArray[1] === 'admin' &&
        pathArray[2] === 'quote-orders' &&
        pathArray[3] === 'view'
      ) {
        return throwError(() => err);
      }

      // Single source of truth for what to show the user.
      // Backend message wins; otherwise SOMETHING_WENT_WRONG.
      const message = extractErrorMessage(err);

      switch (err.status) {
        case 403:
          // Toast only — deliberately NO navigation. This branch used to run
          // `this.router.navigate(['403'])`, and there has never been a `403`
          // route or component in the app, so the `**` wildcard redirected to
          // `/404`: a user sitting on a page they were entitled to see was
          // thrown onto "Page not found" the moment ANY request on that page
          // came back 403. That is the reported "Stock Allocation shows 404" —
          // the shell stays mounted, a red toast appears, and the content area
          // is the NotFoundComponent. The 403 can come from anywhere (the V3
          // CSRF gate, a relayed upstream refusal), including a background
          // poll the user never triggered, which is why it looked intermittent.
          //
          // Evicting the user from the current page is the wrong response to a
          // single failed request in any case: deciding whether a ROUTE may be
          // entered is AuthGuardService's job, not the error interceptor's. So
          // this behaves like every other non-401 status here — report it and
          // let the caller clean up. Do NOT reintroduce a navigate() here; if a
          // real 403 landing page is ever wanted it needs a registered route
          // AND a reason to leave the page, not the wildcard by accident.
          this.alertService.error(message);
          return throwError(() => err);

        case 401:
          // A 401 from the AI API is NOT the console session expiring. That host authenticates with
          // the in-memory service token as a Bearer header — a different credential from the HttpOnly
          // cookie every other request rides on — and the cookie session is routinely still valid
          // when the token is not. Treating the two as one is what made clicking the Pebbl icon log
          // the user out: opening it fires GET /pebbl/models and GET /pebbl/conversations, an aged-out
          // token 401'd both, and this branch tore down a session the server had never rejected.
          //
          // CredentialsInterceptor has already re-minted the token and replayed the request once by
          // the time a 401 reaches here, so this is a genuine AI-side refusal. Report it and leave the
          // user on the page — the AI views degrade on their own (empty list, hidden model picker).
          if (isAiApiUrl(request.url, environment.aiApiUrl)) {
            this.alertService.error(message);
            return throwError(() => err);
          }

          // The session is gone. AuthService.endSession() is the ONE place that tears it down: it
          // clears the client-side auth state and redirects to /login exactly once, whether the
          // burst is one failing request or the ~15 the dashboard fires in parallel, and it does
          // it with the Router rather than a document reload (see endSession for why a reload
          // turned a single expiry into an endless reload loop).
          //
          // Everything below is therefore guarded by "did WE own this teardown": one toast and one
          // shell reset per expiry, not one per failing request. `false` means a teardown is
          // already in flight or we are on a public auth page — the error still propagates so the
          // caller can clear its own loading state, but nothing user-facing happens again.
          // BEFORE tearing anything down, try to renew.
          //
          // This interceptor is registered LAST, so on the response path it runs FIRST — which is
          // why the renewal has to live here rather than in CredentialsInterceptor. By the time a
          // 401 reached that one, endSession() would already have cleared the session and started
          // navigating to /login, and a successful refresh would have had nothing left to save.
          //
          // AuthRefreshService collapses the whole 401 burst into a single refresh call, and the
          // retry marker rides in HttpContext so a replay that 401s again ends the session instead
          // of refreshing forever.
          const alreadyRetried = request.context.get(AUTH_REFRESH_RETRIED);
          // Must name the endpoint ACTUALLY in use. This read 'oauth/refresh-token' while
          // refresh was still on PHP; once it moved to Node the guard stopped matching, so a 401
          // from the refresh call itself would have triggered another refresh — the recursion this
          // check exists to prevent.
          const isRefreshCall = request.url.includes('auth/refresh-token');

          if (!alreadyRetried && !isRefreshCall && !this.authService.isSessionEnding()) {
            return this.authRefreshService.refresh().pipe(
              switchMap((renewed) => renewed
                ? next.handle(request.clone({
                    context: request.context.set(AUTH_REFRESH_RETRIED, true),
                  }))
                : this.endSessionWith(message, err))
            );
          }

          return this.endSessionWith(message, err);

        case 200:
          // Unusual: 200 reaching the error path. Preserve legacy "show as
          // success + redirect to login" behavior.
          this.alertService.success(message);
          this.router.navigate(['/login']);
          return throwError(() => err);

        case 404:
          if (location.pathname.indexOf('/reset-password') !== -1) {
            this.router.navigate(['/login']);
          }
          this.alertService.error(message);
          return throwError(() => err);

        case 400:
        case 405:
        case 406:
        case 408:
        case 413:
        case 415:
        case 500:
        case 501:
        case 502:
        case 503:
        case 504:
          this.alertService.error(message);
          // Several of these previously returned `of(err)` to suppress the
          // error downstream; that hid genuine failures from the caller.
          // Propagate consistently so per-component cleanup (loading flags,
          // disabled states) can run.
          return throwError(() => err);

        default:
          // Unhandled / unexpected status (0, 1xx, 3xx, 5xx out of list, etc.).
          // This is the path that used to call
          //     this.alertService.error("danger", error)
          // — args reversed, so the real backend text became the title and
          // the literal string "danger" became the toast body. Same message
          // pipeline as every other branch now.
          this.alertService.error(message);
          return throwError(() => err);
      }
    }));
  }
}
